import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";
import { identifyUser, resetUser } from '@/lib/posthog';
import { Analytics } from '@/lib/analytics';
import { SOPHIE_TEAM_OVERRIDE_STORAGE_KEY } from '@/lib/http';
import { TeamRole } from '@/types/roles';

const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';

// Helper function for API calls
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  };

  // Add session token to headers if it exists
  const accessToken = localStorage.getItem('sb-access-token');
  const refreshToken = localStorage.getItem('sb-refresh-token');
  
  console.log('Tokens from localStorage:', {
    accessToken: accessToken ? 'present' : 'missing',
    refreshToken: refreshToken ? 'present' : 'missing'
  });

  if (accessToken && refreshToken) {
    defaultOptions.headers = {
      ...defaultOptions.headers,
      'Authorization': `Bearer ${accessToken}`,
      'X-Refresh-Token': refreshToken
    };
    console.log('Headers being sent:', defaultOptions.headers);
  }

  console.log('Making API call to:', `${API_URL}${endpoint}`);
  console.log('With options:', { ...defaultOptions, ...options });

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  });

  console.log('Response status:', response.status);
  console.log('Response headers:', Object.fromEntries(response.headers.entries()));
  console.log('All cookies:', document.cookie);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
};

type User = {
  id: string;
  email: string;
  name: string;
  teamRole: TeamRole;
  created_at: string;
  updated_at: string;
  isSophie: boolean;
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: () => Promise<void>;
  signup: (email: string, password: string, name: string, organisationName?: string, contactInfo?: { phone?: string; address?: string; city?: string; province?: string; country?: string; postalCode?: string }) => Promise<void>;
  completeGoogleSignup: (organisationName: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const checkSession = async () => {
    try {
      const data = await apiCall('/auth/session');
      if (data.success && data.data) {
        setUser(data.data);
        // Identify user in PostHog
        try {
          identifyUser(data.data.id, {
            email: data.data.email,
            name: data.data.name,
            teamRole: data.data.teamRole,
            isSophie: data.data.isSophie,
          });
        } catch (posthogError) {
          console.warn('PostHog identification failed:', posthogError);
        }
        console.log('Session restored:', data.data);
        console.log('User role from session:', data.data.teamRole);
        
        // Check for subscription upgrade after session restoration
        const selectedSubscription = localStorage.getItem('selected-subscription');
        console.log('Session restoration - selectedSubscription:', selectedSubscription);
        console.log('Session restoration - localStorage state:', {
          selectedSubscription,
          currentPath: window.location.pathname,
          isDashboard: window.location.pathname === '/dashboard'
        });
        
        if (selectedSubscription && window.location.pathname === '/dashboard') {
          // Redirect to billing with subscription parameter for upgrade
          console.log('Session restoration - redirecting to billing with subscription:', selectedSubscription);
          window.location.href = `/billing?subscription=${selectedSubscription}`;
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
      // Only clear user if we're not already on the login page and this isn't a network error
      if (window.location.pathname !== '/signin') {
        setUser(null);
      }
    }
  };

  useEffect(() => {
    // Check for active session on mount
    const initSession = async () => {
      try {
        await checkSession();
      } finally {
        setIsLoading(false);
      }
    };

    initSession();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const data = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (data.success && data.data) {
        // Set the session tokens in localStorage
        if (data.message) {
          const tokens = JSON.parse(data.message);
          localStorage.setItem('sb-access-token', tokens.accessToken);
          localStorage.setItem('sb-refresh-token', tokens.refreshToken);
        }
        
        setUser(data.data);
        // Identify user in PostHog
        identifyUser(data.data.id, {
          email: data.data.email,
          name: data.data.name,
          teamRole: data.data.teamRole,
        });
        
        // Track sign in event
        Analytics.trackSignIn('email');
        
        toast.success("Successfully signed in!");
        console.log('Session tokens stored');
        
        // Check for subscription upgrade after successful login
        const selectedSubscription = localStorage.getItem('selected-subscription');
        console.log('Login - selectedSubscription:', selectedSubscription);
        console.log('Login - localStorage state:', {
          selectedSubscription,
          hasAccessToken: !!localStorage.getItem('sb-access-token'),
          hasRefreshToken: !!localStorage.getItem('sb-refresh-token')
        });
        
        const defaultDestination = data.data.isSophie == true ? '/dashboard' : '/';

        if (selectedSubscription) { // && data.data.teamRole !== 'sophieAdmin') {
          // Navigate to billing with subscription parameter for upgrade
          console.log('Login - navigating to billing with subscription:', selectedSubscription);
          navigate(`/billing?subscription=${selectedSubscription}`);
        } else {
          // Navigate to dashboard if no subscription upgrade needed
          console.log('Login - navigating to default destination', { defaultDestination });
          navigate(defaultDestination);
        }
      } else {
        throw new Error(data.message || "Failed to sign in");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign in";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async () => {
    try {
      setIsLoading(true);
      
      // Track Google sign in attempt
      Analytics.trackSignIn('google');
      
      // Instead of directly using Supabase, we'll initiate the OAuth flow via our backend
      
      // Store current path for redirect after authentication, preserving subscription parameter
      const selectedSubscription = localStorage.getItem('selected-subscription');
      const redirectPath = selectedSubscription ? `/billing?subscription=${selectedSubscription}` : '/';
      localStorage.setItem('auth-redirect-path', redirectPath);
      
      console.log('Google login - setting auth-redirect-path:', redirectPath);
      
      // Redirect to backend Google OAuth endpoint
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const googleAuthUrl = `${API_URL}/auth/google?redirecturl=${encodeURIComponent(redirectUrl)}`;
      
      // Redirect to Google OAuth via backend
      window.location.href = googleAuthUrl;
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign in with Google";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string, organisationName?: string, contactInfo?: { phone?: string; address?: string; city?: string; province?: string; country?: string; postalCode?: string }) => {
    try {
      setIsLoading(true);
      
      // Get subscription from localStorage if available
      const selectedSubscription = localStorage.getItem('selected-subscription');
      const subscription = selectedSubscription ? parseInt(selectedSubscription) : undefined;
      
      await apiCall('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ 
          email, 
          password, 
          name, 
          organisationName,
          subscription,
          phone: contactInfo?.phone,
          address: contactInfo?.address,
          city: contactInfo?.city,
          province: contactInfo?.province,
          country: contactInfo?.country,
          postalCode: contactInfo?.postalCode,
        }),
      });

      // Track sign up event
      Analytics.trackSignUp('email');

      toast.success("Account created successfully! Please check your email to verify your account.");
      navigate('/auth/email-verification-pending');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to create account";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeGoogleSignup = async (organisationName: string) => {
    try {
      setIsLoading(true);
      
      // Get subscription from localStorage if available
      const selectedSubscription = localStorage.getItem('selected-subscription');
      const subscription = selectedSubscription ? parseInt(selectedSubscription) : undefined;
      
      const data = await apiCall('/auth/google-signup', {
        method: 'POST',
        body: JSON.stringify({ 
          organisationName,
          subscription 
        }),
      });

      if (data.success) {
        await checkSession(); // Get fresh session data from backend
        
        // Track Google sign up completion
        Analytics.trackSignUp('google');
        
        toast.success("Account created successfully!");
        
        // Handle subscription upgrade if selected during signup
        const selectedSubscription = localStorage.getItem('selected-subscription');
        if (selectedSubscription) {
          // Navigate to billing with subscription parameter for upgrade
          navigate(`/billing?subscription=${selectedSubscription}`);
        } else {
          navigate('/');
        }
      } else {
        throw new Error(data.message || "Failed to complete sign up");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to complete sign up";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiCall('/auth/logout', {
        method: 'POST',
      });

      // Clear tokens from localStorage
      localStorage.removeItem('sb-access-token');
      localStorage.removeItem('sb-refresh-token');
      sessionStorage.removeItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY);
      
      // Reset user in PostHog
      resetUser();
      
      // Track sign out event
      Analytics.trackSignOut();
      
      setUser(null);
      toast.info("You have been signed out");
      navigate('/signin');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign out";
      toast.error(errorMessage);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      await apiCall('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      toast.success("Password reset link has been sent to your email");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send reset link";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (newPassword: string) => {
    try {
      setIsLoading(true);
      await apiCall('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      });

      toast.success("Password changed successfully");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to change password";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async () => {
    try {
      setIsLoading(true);
      await apiCall('/auth/delete-account', {
        method: 'DELETE',
      });

      toast.success("Account deleted successfully");
      await logout();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete account";
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user, 
      isLoading, 
      login,
      googleLogin,
      signup,
      completeGoogleSignup,
      logout,
      resetPassword,
      changePassword,
      deleteAccount
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
